/* eslint-disable */

"use client";;
import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useErrorMessage } from '@/src/hooks/useErrorMessage';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import PageHeader from '../ui/PageHeader';
import { BreadcrumbItem } from '../ui/Breadcrumb';
import { CartItemComponent } from './CartItem';
import { CartSummaryComponent } from './CartSummary';
import {
  CartContainer,
  CardWrapper,
  EmptyCartMessage,
  EmptyCartIcon,
  MessageText,
  ActionButton
} from './CartStyledComponents';
import { useCartLogic } from './useCartLogic';
import { AccountPromoBanner } from './AccountPromoBanner';
import { RAFFLE_CONFIG } from '../../../constants';
import { ShoppingCartIcon, ArrowBackIcon, CloseIcon } from '../icons';
import styles from './Cart.module.css';

export const Cart: React.FC = () => {
  const t = useTranslations('cart');
  const tCommon = useTranslations('common');
  const resolveError = useErrorMessage();
  const cartLogic = useCartLogic();
  const searchParams = useSearchParams();
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    const urlCartId = searchParams?.get('id');
    if (urlCartId) {
      console.log('🔗 Cart recovery: Found cart ID in URL:', urlCartId);
      cartLogic.loadCartFromUrl(urlCartId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (cartLogic.cartError) {
      setToastVisible(true);
      const timer = setTimeout(() => {
        setToastVisible(false);
        cartLogic.clearCartError();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [cartLogic.cartError]);

  const cartBreadcrumbs: BreadcrumbItem[] = [
    { label: tCommon('breadcrumb.home'), path: '/' },
    { label: t('breadcrumb.cart') }
  ];

  if (!cartLogic.isMounted || (cartLogic.isLoading && !cartLogic.cart)) {
    console.log('⏳ Showing loading state - cart is loading or not mounted');
    return (
      <>
        <PageHeader
          title={t('page.title')}
          icon={<ShoppingCartIcon />}
          breadcrumbs={cartBreadcrumbs}
        />
        <CartContainer>
          <CardWrapper elevation={0}>
            <EmptyCartMessage>
              <span className={styles.spinnerLarge} />
              <MessageText>{t('page.loading')}</MessageText>
            </EmptyCartMessage>
          </CardWrapper>
        </CartContainer>
      </>
    );
  }

  if (cartLogic.isCartEmpty) {
    console.log('🛒 Cart is empty, showing empty state');
    return (
      <>
        <PageHeader
          title={t('page.title')}
          icon={<ShoppingCartIcon />}
          breadcrumbs={cartBreadcrumbs}
        />
        <CartContainer>
          <CardWrapper elevation={0}>
            <EmptyCartMessage>
              <EmptyCartIcon />
              <MessageText>{t('page.empty')}</MessageText>
              <div style={{ maxWidth: 300 }}>
                <Link href="/" style={{ textDecoration: 'none' }}>
                  <ActionButton variant="outline">
                    <ArrowBackIcon style={{ width: 18, height: 18 }} />
                    {t('page.continueShopping')}
                  </ActionButton>
                </Link>
              </div>
            </EmptyCartMessage>
          </CardWrapper>
        </CartContainer>
      </>
    );
  }

  const getTranslatedError = () => {
    if (cartLogic.cartErrorCode) {
      return resolveError({ errorCode: cartLogic.cartErrorCode }, { max: 10 });
    }
    return cartLogic.cartError || resolveError(null);
  };

  const getRaffleEndDate = (): string => {
    const now = new Date();
    const dayOfWeek = now.getUTCDay();
    const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
    const end = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + daysUntilSunday,
      23, 59, 0
    ));
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[end.getUTCDay()]}, ${months[end.getUTCMonth()]} ${end.getUTCDate()} · 23:59 UTC`;
  };

  const cartTotal = cartLogic.getTotalPrice();
  const raffleQualified = cartTotal >= RAFFLE_CONFIG.minCartValueUsd;
  const raffleRemaining = Math.ceil(RAFFLE_CONFIG.minCartValueUsd - cartTotal);

  return (
    <>
      {console.log('🎨 Cart main render started')}
      {toastVisible && cartLogic.cartError && (
        <div className={styles.toast}>
          <div className={styles.toastAlert}>
            {getTranslatedError()}
            <button className={styles.toastClose} onClick={() => { setToastVisible(false); cartLogic.clearCartError(); }}>
              <CloseIcon style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </div>
      )}
      <PageHeader
        title={t('page.title')}
        icon={<ShoppingCartIcon />}
        breadcrumbs={cartBreadcrumbs}
      />
      <CartContainer>
        <div>
          <div>
            {(() => {
              console.log('🗂️ Rendering cart items:', cartLogic.cartItems.length);
              return null;
            })()}
            {cartLogic.cartItems.map((item, index) => (
              <CartItemComponent
                key={`${item.productId}-${item.selectedValue}`}
                item={item}
                index={index}
                cartPricing={cartLogic.cartPricing}
                allItems={cartLogic.cartItems}
                onQuantityChange={cartLogic.handleQuantityChange}
                onRemoveItem={cartLogic.removeFromCart}
                onProductClick={cartLogic.handleProductClick}
              />
            ))}
          </div>

          <div className={`${styles.raffleBannerCart} ${raffleQualified ? styles.raffleBannerCartQualified : ''}`}>
            <span className={styles.raffleBannerCartIcon}>{raffleQualified ? '🎉' : '🎟️'}</span>
            <div className={styles.raffleBannerCartContent}>
              <p className={`${styles.raffleBannerCartTitle} ${raffleQualified ? styles.raffleBannerCartTitleQualified : ''}`}>
                {t('raffle.title')}
              </p>
              <p className={styles.raffleBannerCartText}>
                {raffleQualified
                  ? `${t('raffle.qualified')} ${t('raffle.qualifiedSub', { prize: RAFFLE_CONFIG.prizeValueUsd })}`
                  : t('raffle.needMore', { remaining: raffleRemaining, prize: RAFFLE_CONFIG.prizeValueUsd })}
              </p>
              <p className={styles.raffleBannerCartEnds}>{t('raffle.ends', { date: getRaffleEndDate() })}</p>
            </div>
          </div>

          <AccountPromoBanner />

          <CardWrapper elevation={0}>
            <div className={styles.summaryInner}>
              <CartSummaryComponent
                cartPricing={cartLogic.cartPricing}
                cartItems={cartLogic.cartItems}
                selectedCurrency={cartLogic.selectedCurrency}
                couponCode={cartLogic.couponCode}
                setCouponCode={cartLogic.setCouponCode}
                isLoadingPricing={cartLogic.isLoadingPricing}
                couponError={cartLogic.couponError}
                setCouponError={cartLogic.setCouponError}
                pricingError={cartLogic.pricingError}
                minOrderError={cartLogic.minOrderError}
                email={cartLogic.email}
                emailError={cartLogic.emailError}
                emailSaved={cartLogic.emailSaved}
                emailCheck={cartLogic.emailCheck}
                giftEmailCheck={cartLogic.giftEmailCheck}
                expressConsent={cartLogic.expressConsent}
                setExpressConsent={cartLogic.setExpressConsent}
                subscribeToNewsletter={cartLogic.subscribeToNewsletter}
                setSubscribeToNewsletter={cartLogic.setSubscribeToNewsletter}
                buyAsGift={cartLogic.buyAsGift}
                setBuyAsGift={cartLogic.setBuyAsGift}
                giftEmail={cartLogic.giftEmail}
                giftEmailError={cartLogic.giftEmailError}
                giftMessage={cartLogic.giftMessage}
                orderLoading={cartLogic.orderLoading}
                isFormValid={cartLogic.isFormValid}
                paymentMethod={cartLogic.paymentMethod}
                setPaymentMethod={cartLogic.setPaymentMethod}
                payOption={cartLogic.payOption}
                setPayOption={cartLogic.setPayOption}
                isAuthenticated={cartLogic.isAuthenticated}
                authRequiredCards={cartLogic.authRequiredCards}
                authRequiredBlocked={cartLogic.authRequiredBlocked}
                paymentRestrictedCards={cartLogic.paymentRestrictedCards}
                blockedPaymentTiles={cartLogic.blockedPaymentTiles}
                unavailablePaymentTiles={cartLogic.unavailablePaymentTiles}
                hiddenPaymentTiles={cartLogic.hiddenPaymentTiles}
                paymentTilesReady={cartLogic.paymentTilesReady}
                paymentNeedsLogin={cartLogic.paymentNeedsLogin}
                noPaymentPossible={cartLogic.noPaymentPossible}
                amountRuleLoginRequired={cartLogic.amountRuleLoginRequired}
                userBalanceCents={cartLogic.userBalanceCents}
                isEmailVerified={cartLogic.isEmailVerified}
                balanceApplyCents={cartLogic.balanceApplyCents}
                setBalanceApplyCents={cartLogic.setBalanceApplyCents}
                onApplyCoupon={cartLogic.handleApplyCoupon}
                onEmailChange={cartLogic.handleEmailChange}
                onGiftEmailChange={cartLogic.handleGiftEmailChange}
                onGiftMessageChange={cartLogic.handleGiftMessageChange}
                onCheckout={cartLogic.handleCheckout}
                turnstileWidget={cartLogic.turnstileWidget}
                otpChallenge={cartLogic.otpChallenge}
                onOtpClose={cartLogic.closeOtpChallenge}
                onOtpVerified={cartLogic.handleOtpVerified}
                kycChallenge={cartLogic.kycChallenge}
                onKycClose={cartLogic.closeKycChallenge}
                onKycVerified={cartLogic.handleKycVerified}
                profileEmail={cartLogic.profileEmail}
                profilePhone={cartLogic.profilePhone}
                payerBankAccountId={cartLogic.payerBankAccountId}
                setPayerBankAccountId={cartLogic.setPayerBankAccountId}
                payerGateError={cartLogic.payerGateError}
                isFullyCoveredByBalance={cartLogic.balanceFullyCovers}
                onFullyCoveredChange={cartLogic.setBalanceFullyCovers}
              />
            </div>
          </CardWrapper>
        </div>
      </CartContainer>
    </>
  );
};

export default Cart;
